import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { pushNotification } from "../../../features/notificationSlice";
import { downloadBlob } from "../downloadBlob";
import { GridSelection } from "./gridGeometry";
import { selectionToMatrix } from "./selectionSummary";
import {
  exportAllSheets,
  exportView,
  ExportFormat,
  matrixToTsv,
  SheetExportPart,
  viewToAoa,
} from "./sheetExport";
import { CompiledView, SheetSource } from "./sheetTypes";

/**
 * Everything the preview can hand back to a person: the view as a file, the
 * whole workbook as a file, or a block of cells on the clipboard. Kept out of
 * SheetView because it is all plumbing - state, notifications and blobs - and
 * none of it is layout.
 */

export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for a page served over plain http, where the async API is absent.
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

interface UseSheetExportArgs {
  source: SheetSource;
  view: CompiledView;
  includeHeader: boolean;
  fileName: string;
  /** Null for a single-sheet file; the tab name otherwise. */
  sheetLabel: string | null;
  sheetCount: number;
  buildAllSheetExports: () => SheetExportPart[];
  selection: GridSelection | null;
}

export interface SheetExportApi {
  exporting: boolean;
  exportCurrentView: (format: ExportFormat) => void;
  exportWorkbook: () => void;
  copyView: () => void;
  copySelection: () => void;
  copyValue: (value: string) => void;
}

export function useSheetExport({
  source,
  view,
  includeHeader,
  fileName,
  sheetLabel,
  sheetCount,
  buildAllSheetExports,
  selection,
}: UseSheetExportArgs): SheetExportApi {
  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  const notify = useCallback(
    (message: string, severity: "success" | "error") => {
      dispatch(pushNotification({ message, severity }));
    },
    [dispatch],
  );

  const failed = useCallback(
    (error: unknown) => {
      notify(
        error instanceof Error
          ? `Export failed: ${error.message}`
          : "Export failed",
        "error",
      );
    },
    [notify],
  );

  const exportCurrentView = useCallback(
    (format: ExportFormat) => {
      setExporting(true);
      void exportView({
        source,
        view,
        format,
        includeHeader,
        originalFileName: fileName,
        sheetLabel,
      })
        .then((result) => {
          downloadBlob(result.blob, result.fileName);
          notify(
            `Exported ${result.rowCount.toLocaleString()} rows x ${result.columnCount} columns to ${result.fileName}`,
            "success",
          );
        })
        .catch(failed)
        .finally(() => setExporting(false));
    },
    [source, view, includeHeader, fileName, sheetLabel, notify, failed],
  );

  const exportWorkbook = useCallback(() => {
    setExporting(true);
    void exportAllSheets(buildAllSheetExports(), fileName)
      .then((result) => {
        downloadBlob(result.blob, result.fileName);
        notify(
          `Exported ${sheetCount} sheets (${result.rowCount.toLocaleString()} rows) to ${result.fileName}`,
          "success",
        );
      })
      .catch(failed)
      .finally(() => setExporting(false));
  }, [buildAllSheetExports, fileName, sheetCount, notify, failed]);

  const copyView = useCallback(() => {
    const matrix = viewToAoa(source, view, includeHeader);
    void copyText(matrixToTsv(matrix))
      .then(() =>
        notify(
          `Copied ${view.rows.length.toLocaleString()} rows x ${view.columns.length} columns`,
          "success",
        ),
      )
      .catch(() => notify("Could not copy to the clipboard", "error"));
  }, [source, view, includeHeader, notify]);

  const copySelection = useCallback(() => {
    if (!selection) return;
    void copyText(matrixToTsv(selectionToMatrix(view, selection)))
      .then(() => notify("Copied the selection", "success"))
      .catch(() => notify("Could not copy to the clipboard", "error"));
  }, [selection, view, notify]);

  const copyValue = useCallback(
    (value: string) => {
      void copyText(value)
        .then(() => notify("Copied the cell value", "success"))
        .catch(() => notify("Could not copy to the clipboard", "error"));
    },
    [notify],
  );

  return {
    exporting,
    exportCurrentView,
    exportWorkbook,
    copyView,
    copySelection,
    copyValue,
  };
}
